import { FilterValues } from "@kaltura/mediaspace-shared-types";
import { BreadcrumbItem, Breadcrumbs, breadcrumbsClasses } from "@kaltura/ds-react-components";
import { useFoldersStore } from "../foldersStore";
import styled from "@emotion/styled";
import FolderActionsMenu from "../folder-actions-menu/FolderActionsMenu";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { Folder, UNFILED_FOLDER_ID } from "../types";
import FolderInfoPanelSkeleton from "./FolderInfoPanelSkeleton";
import { ButtonAnalyticsProvider } from "@kaltura/mediaspace-shared-hooks";
import { useEffect } from "react";

export interface FolderInfoPanelProps {
    /**
     * array of all folder objects
     * used to initialize the zustand folders store with
     * initial folders as received from the server
     */
    folders: Folder[];
    /** currently selected filter values */
    filterValues: FilterValues;
    /** callback for selecting a breadcrumb filter */
    onChange: (values: FilterValues) => void;
}

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme }) => ({
    [`& .${breadcrumbsClasses.li}`]: {
        lineHeight: theme.typography.pxToRem(24),
    },
}));

const StyledActiveItemContainer = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
}));

export function FolderInfoPanel({ folders, filterValues, onChange }: FolderInfoPanelProps) {
    const selectedFolderId = typeof filterValues.folderId === "string" ? filterValues.folderId : "";
    const isLoadingFolders = useFoldersStore((state) => state.isLoadingFolders);
    const foldersMap = useFoldersStore((state) => state.foldersMap);
    const setFolders = useFoldersStore((state) => state.setFolders);
    const selectedFolder = foldersMap.get(selectedFolderId);

    // update initial folders in the store by folders prop when component mounts
    useEffect(() => {
        setFolders(folders);
    }, []);

    // do not render if no folder is selected
    if (selectedFolderId === "") return null;

    const myMediaBreadcrumb: BreadcrumbItem = {
        label: translate("My Media"),
        onClick: () => onChange({ ...filterValues, folderId: "" }),
    };

    const getBreadcrumbsItems = (): BreadcrumbItem[] => {
        if (selectedFolderId === UNFILED_FOLDER_ID) {
            return [
                {
                    label: translate("Unfiled media"),
                    active: true,
                },
            ];
        }

        if (!selectedFolder) return [];

        return selectedFolder.fullPathIds.map((id, index) => {
            const isLast = index === selectedFolder.fullPathIds.length - 1;
            return {
                label: !isLast ? (
                    selectedFolder.fullPathNames[index]
                ) : (
                    <StyledActiveItemContainer>
                        {selectedFolder.fullPathNames[index]}
                        <FolderActionsMenu
                            selectedFolder={selectedFolder}
                            onDeleteSubmitEnd={() => onChange({ ...filterValues, folderId: "" })}
                        />
                    </StyledActiveItemContainer>
                ),
                active: isLast,
                onClick: !isLast
                    ? () => onChange({ ...filterValues, folderId: selectedFolder.fullPathIds[index] })
                    : undefined,
            };
        });
    };

    const breadcrumbsItems = getBreadcrumbsItems();

    return (
        <ButtonAnalyticsProvider applicationFeature={"folders"}>
            <div>
                {isLoadingFolders ? (
                    <FolderInfoPanelSkeleton />
                ) : (
                    <StyledBreadcrumbs
                        ariaLabel={translate("Folder navigation")}
                        items={[myMediaBreadcrumb, ...breadcrumbsItems]}
                    />
                )}
            </div>
        </ButtonAnalyticsProvider>
    );
}

export default FolderInfoPanel;
